import React, { useMemo } from "react";
import { formatStoredDate } from "../../../Utils/formatStoredDate";
import styles from "./styles/AssetEvaluation.module.css";
import { formatCompactMoney } from "../../../Utils/assetEvaluationUtils";

/*
  Hand-rolled SVG line/area chart for the Asset Evaluation section.

  Written by hand rather than pulling in a charting library: the project has no
  chart dependency today, and the design is specific enough (smooth curve,
  gradient fill, pill value labels, rotated date ticks) that a bespoke SVG
  matches it more closely than configuring a general-purpose library.

  It plots up to two series — asset values in green, debt values in red — on one
  shared axis. See `buildScale` for how the sign is handled.

  Points are spaced by index over the *union* of both series' dates, not by
  elapsed time: the design shows evenly spaced ticks even where two records are
  two days apart, and sharing the index keeps the two lines aligned.
*/

/*
  viewBox is sized to the card's real display size in the desktop design
  (~486x270 CSS px) so that font sizes below are 1:1 with the design instead of
  being scaled down by the viewBox-to-container ratio.
*/
const VB_W = 486;
const VB_H = 270;
/*
  `left` is a floor, not a fixed value — see `leftFor` below. The other three
  are fixed.
*/
const M = { top: 40, right: 14, bottom: 62, left: 58 };

const PLOT_H = VB_H - M.top - M.bottom;

/* ------------------------------------------------------- the left margin */
/*
  The rotated axis title is centred on x = 13 at 10px, so it occupies roughly
  8..18. The tick labels are right-anchored just inside the plot and grow
  leftwards from there, and how far they grow depends on the currency: "700K €"
  is one thing, "700K CHF" or "CA$700K" quite another. With a fixed left margin
  a long one simply ran into the title.

  So the margin is measured instead: the title's band, a gap that never closes,
  the widest tick this chart actually draws, and the space between that tick and
  the axis. The gap is the point — whatever the currency does, it stays.
*/
const AXIS_TITLE_BAND = 18;
const TITLE_TICK_GAP = 10;
const TICK_AXIS_GAP = 8;
// Advance width of a digit at the 9.5px face the ticks are drawn in.
const TICK_CHAR_W = 5.6;

const leftFor = (ticks, currencyCode, formatTick) => {
  const widest = ticks.reduce(
    (max, tick) => Math.max(max, String(formatTick(tick, currencyCode)).length),
    0
  );
  return Math.max(
    M.left,
    AXIS_TITLE_BAND +
      TITLE_TICK_GAP +
      Math.ceil(widest * TICK_CHAR_W) +
      TICK_AXIS_GAP
  );
};

/*
  Compact axis/label formatting: 665000 in euros -> "665K \u20ac", in dollars ->
  "$665K". Where the symbol goes follows the currency rather than always
  trailing the number, which is why this delegates to the shared formatter
  instead of concatenating: "665K$" is simply wrong in English. One decimal,
  matching the density the design's ticks were drawn at.
*/
function formatCompact(value, currencyCode) {
  return formatCompactMoney(value, currencyCode, 1) || "";
}

// "2024-06-01" -> "01.06.2024" (the format used in the design)
/*
  A tick's date, in the reader's own order -- the same formatter the history
  table under this chart uses, so a point and its row read alike.
*/
const formatDate = (value, langLabel) => formatStoredDate(value, langLabel);

/*
  Round the axis out to sensible whole steps around the data range.

  `includeZero` is what makes the two-series case work. Assets are positive and
  debts are negative, so with both on screen the axis has to be signed and span
  0 — otherwise one series sits off the plot entirely. Forcing 0 into the domain
  also guarantees the zero line (drawn solid by the caller) is a real tick, so
  green reads as "above the line" and red as "below" it.

  With only one series that padding would be wasted: an asset series sitting
  around 650K would be squashed into the top tenth of the plot for no reason. So
  0 is only forced in when both series are present.
*/
function buildScale(values, includeZero) {
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (includeZero) {
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    return niceTicks(min - pad, max + pad);
  }
  const pad = (max - min) * 0.25;
  return niceTicks(min - pad, max + pad);
}

function niceTicks(lo, hi) {
  const rawStep = (hi - lo) / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep) || 1)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rawStep) || mag * 10;
  const start = Math.floor(lo / step) * step;
  const end = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = start; v <= end + step / 2; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return { min: start, max: end, ticks };
}

// Catmull-Rom through the points, emitted as cubic beziers.
function smoothPath(pts) {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
  const t = 0.2;
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) * t;
    const c1y = p1.y + (p2.y - p0.y) * t;
    const c2x = p2.x - (p3.x - p1.x) * t;
    const c2y = p2.y - (p3.y - p1.y) * t;
    d += ` C ${r(c1x)} ${r(c1y)}, ${r(c2x)} ${r(c2y)}, ${r(p2.x)} ${r(p2.y)}`;
  }
  return d;
}

const r = (n) => Math.round(n * 100) / 100;

const isPlottable = (rec) =>
  rec &&
  rec.date &&
  rec.value !== "" &&
  rec.value !== null &&
  rec.value !== undefined &&
  !Number.isNaN(Number(rec.value));

/*
  `series`: [{ key, records, label, color, gradientId, labelsBelow }]
  Empty series are dropped; the chart renders its empty state only when every
  series is empty.
*/
function EvaluationChart({
  series = [],
  currencyCode,
  title,
  yAxisLabel,
  xAxisLabel,
  emptyText,
  // "fr" puts a tick's date in French order; anything else reads as English.
  langLabel,
}) {
  const model = useMemo(() => {
    const cleaned = series
      .map((s) => ({ ...s, records: (s.records || []).filter(isPlottable) }))
      .filter((s) => s.records.length > 0);
    if (cleaned.length === 0) return null;

    /*
      One shared x domain: every distinct date across both series, in order.
      A record lands on the index of its own date, so a point that exists in
      only one series leaves a gap in the other rather than shifting it.
    */
    const dates = Array.from(
      new Set(cleaned.flatMap((s) => s.records.map((rec) => String(rec.date))))
    ).sort();
    const indexOfDate = new Map(dates.map((d, i) => [d, i]));

    const values = cleaned.flatMap((s) => s.records.map((rec) => Number(rec.value)));
    const scale = buildScale(values, cleaned.length > 1);
    const span = scale.max - scale.min || 1;

    /*
      Measured from the ticks this chart is actually going to draw, so the gap
      to the axis title holds whatever the currency turns out to be. The plot
      gives up the width the labels need rather than the labels overlapping.
    */
    const left = leftFor(scale.ticks, currencyCode, formatCompact);
    const plotW = VB_W - left - M.right;

    const xFor = (i) =>
      dates.length === 1 ? left + plotW / 2 : left + (plotW * i) / (dates.length - 1);
    const yFor = (v) => M.top + PLOT_H - ((v - scale.min) / span) * PLOT_H;

    const plotted = cleaned.map((s) => ({
      ...s,
      pts: s.records
        .map((rec) => ({
          x: xFor(indexOfDate.get(String(rec.date))),
          y: yFor(Number(rec.value)),
          value: Number(rec.value),
          date: rec.date,
        }))
        .sort((a, b) => a.x - b.x),
    }));

    /*
      The area fills to the zero line when it is on screen, so a debt series
      hangs down from 0 instead of being filled from the bottom of the plot.
    */
    const zeroOnAxis = scale.min <= 0 && scale.max >= 0;
    const baseY = zeroOnAxis ? yFor(0) : M.top + PLOT_H;

    return { plotted, scale, yFor, dates, xFor, baseY, left, plotW };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, currencyCode]);

  if (!model) {
    return <div className={styles.chart_empty}>{emptyText}</div>;
  }

  const { plotted, scale, yFor, dates, xFor, baseY, left, plotW } = model;

  return (
    <svg
      className={styles.chart_svg}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      role="img"
      aria-label={title}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {plotted.map((s) => (
          <linearGradient
            key={s.gradientId}
            id={s.gradientId}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor={s.color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={s.color} stopOpacity="0.03" />
          </linearGradient>
        ))}
      </defs>

      <text x={VB_W / 2} y={14} textAnchor="middle" fontSize="13" fontWeight="600" fill="#252F39">
        {title}
      </text>

      {/* Legend — only worth the row when there is more than one series. */}
      {plotted.length > 1 && (
        <g>
          {plotted.map((s, i) => {
            const gap = 108;
            const startX = VB_W / 2 - ((plotted.length - 1) * gap) / 2;
            const x = startX + i * gap;
            return (
              <g key={s.key} transform={`translate(${x} 28)`}>
                <line x1={-46} y1={-3} x2={-32} y2={-3} stroke={s.color} strokeWidth="2.4" strokeLinecap="round" />
                <circle cx={-39} cy={-3} r="3" fill={s.color} stroke="#FFFFFF" strokeWidth="1.2" />
                <text x={-27} y={0} fontSize="9.5" fill="#252F39">
                  {s.label}
                </text>
              </g>
            );
          })}
        </g>
      )}

      {/* y axis */}
      <text
        transform={`translate(13 ${M.top + PLOT_H / 2}) rotate(-90)`}
        textAnchor="middle"
        fontSize="10"
        fill="#252F39"
      >
        {`${yAxisLabel} (${currencyCode})`}
      </text>

      {scale.ticks.map((tick) => {
        const y = yFor(tick);
        const isZero = tick === 0;
        return (
          <g key={tick}>
            <line
              x1={left}
              y1={y}
              x2={left + plotW}
              y2={y}
              stroke={isZero ? "#B6BDC5" : "#E6E8EB"}
              strokeWidth="1"
              strokeDasharray={isZero ? undefined : "2 4"}
            />
            <text
              x={left - TICK_AXIS_GAP}
              y={y + 3}
              textAnchor="end"
              fontSize="9.5"
              fontWeight={isZero ? "600" : undefined}
              fill="#252F39"
            >
              {formatCompact(tick, currencyCode)}
            </text>
          </g>
        );
      })}

      {/* Areas and lines first, so the points and pills sit on top of both. */}
      {plotted.map((s) => {
        const line = smoothPath(s.pts);
        const area = `${line} L ${r(s.pts[s.pts.length - 1].x)} ${r(baseY)} L ${r(
          s.pts[0].x
        )} ${r(baseY)} Z`;
        return (
          <g key={`${s.key}_path`}>
            <path d={area} fill={`url(#${s.gradientId})`} />
            <path d={line} fill="none" stroke={s.color} strokeWidth="1.8" strokeLinecap="round" />
          </g>
        );
      })}

      {plotted.map((s) =>
        s.pts.map((p, i) => {
          const label = formatCompact(p.value, currencyCode);
          const boxW = label.length * 5.4 + 8;
          /*
            Asset pills sit above their point and debt pills below, so the two
            series never fight for the same strip of the plot. Each still flips
            to the other side rather than clipping out of the plot.
          */
          const preferBelow = Boolean(s.labelsBelow);
          const roomBelow = M.top + PLOT_H - p.y > 24;
          const roomAbove = p.y - M.top > 22;
          const below = preferBelow ? roomBelow || !roomAbove : !roomAbove && roomBelow;
          const boxY = below ? p.y + 9 : p.y - 22;
          // Keep the pill inside the plot horizontally at the first/last point.
          const boxX = Math.min(
            Math.max(p.x - boxW / 2, left - 6),
            left + plotW + 6 - boxW
          );
          return (
            <g key={`${s.key}-${p.date}-${i}`}>
              <rect x={boxX} y={boxY} width={boxW} height={14} rx="3" fill="#F2F4F7" />
              <text
                x={boxX + boxW / 2}
                y={boxY + 10}
                textAnchor="middle"
                fontSize="9"
                fill="#252F39"
              >
                {label}
              </text>
              <circle cx={p.x} cy={p.y} r="3.5" fill={s.color} stroke="#FFFFFF" strokeWidth="1.2" />
            </g>
          );
        })
      )}

      {/* Date ticks — one per date in the shared domain, not per series. */}
      {dates.map((date, i) => (
        <g key={date}>
          <line
            x1={xFor(i)}
            y1={M.top + PLOT_H}
            x2={xFor(i)}
            y2={M.top + PLOT_H + 4}
            stroke="#B0B0B0"
            strokeWidth="1"
          />
          <text
            transform={`translate(${xFor(i)} ${M.top + PLOT_H + 14}) rotate(-30)`}
            textAnchor="end"
            fontSize="9"
            fill="#252F39"
          >
            {formatDate(date, langLabel)}
          </text>
        </g>
      ))}

      {/* The plot floor, which the date ticks hang from. */}
      <line
        x1={left}
        y1={M.top + PLOT_H}
        x2={left + plotW}
        y2={M.top + PLOT_H}
        stroke="#D8DCE0"
        strokeWidth="1"
      />

      <text
        x={left + plotW / 2}
        y={VB_H - 8}
        textAnchor="middle"
        fontSize="10"
        fill="#252F39"
      >
        {xAxisLabel}
      </text>
    </svg>
  );
}

export default EvaluationChart;
